import { UnstyledButton, Center, Table, Group } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { clsx } from 'clsx';
import { createRef, ReactNode, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './Header.module.css';
import { IFieldDescription, IRowData } from './interfaces';
import { SelectionContext } from './SelectionContextProvider';

type ThProps = {
    children: ReactNode;
    reversed: boolean;
    withSort: boolean;
    sorted: boolean;
    onSort: () => void;
};

const Th = ({ children, reversed, sorted, onSort, withSort }: ThProps) => {
    const icon = sorted ? (reversed ? 'keyboard_arrow_up' : 'keyboard_arrow_down') : 'unfold_more';

    const handleSort = onSort;

    return (
        <Table.Th className="data-table-cell">
            <UnstyledButton className={classes.control} onClick={handleSort}>
                <Group>
                    {children}
                    {withSort && (
                        <Center className={classes.icon}>
                            <Icon icon={icon} size="sm" />
                        </Center>
                    )}
                </Group>
            </UnstyledButton>
        </Table.Th>
    );
};

type Props<TRowType extends IRowData> = {
    onToggleAll(): void;
    withSort: boolean;
    fieldDescriptions: IFieldDescription<TRowType>[];
    reverseSortDirection: boolean;
    sortBy?: string | null;
    data: TRowType[];
    onSort: (key: string) => void;
    withRowActionsColumn: boolean;
};

export const Header = <TRowType extends IRowData>({
    onToggleAll,
    sortBy,
    data,
    withSort,
    onSort,
    reverseSortDirection,
    fieldDescriptions,
    withRowActionsColumn,
}: Props<TRowType>) => {
    const { selectedItemKeys, selectionEnabled } = useContext(SelectionContext);
    const handleSort = (sortKey: string) => onSort(sortKey);
    const { t } = useTranslation();

    const selectRef = createRef<HTMLInputElement>();

    useEffect(() => {
        if (selectRef && selectRef.current) {
            selectRef.current.indeterminate = selectedItemKeys.length > 0 && selectedItemKeys.length !== data.length;
        }
    }, [data.length, selectRef, selectedItemKeys.length]);

    return (
        <Table.Thead>
            <Table.Tr className={clsx({ ['data-table-header-selectable']: selectionEnabled })}>
                {selectionEnabled && (
                    <Table.Th className="data-table-cell">
                        <input
                            ref={selectRef}
                            checked={selectedItemKeys.length === data.length}
                            className="data-table-select-checkbox"
                            readOnly={true}
                            type="checkbox"
                            onChange={() => onToggleAll()}
                        />
                    </Table.Th>
                )}

                {fieldDescriptions.map((key) => (
                    <Th
                        key={key.data}
                        reversed={reverseSortDirection}
                        sorted={sortBy === key.data}
                        withSort={withSort}
                        onSort={() => withSort && handleSort(key.data)}
                    >
                        {key.header}
                    </Th>
                ))}
                {/* Column for action rows indicator */}
                {withRowActionsColumn && <th className="data-table-cell data-table-cell-revert">{t('actions')}</th>}
            </Table.Tr>
        </Table.Thead>
    );
};
